import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { type ErrorType } from "@vega-api/configs/customClientQuery";
import { useUpdateDealShortlistItem } from "@vega-api/generated/coinvest-deal-shortlist/coinvest-deal-shortlist";
import {
  getGetDealTrackingForGpQueryKey,
  useUpdateDealOpportunityTracking,
} from "@vega-api/generated/coinvest-deal-tracking/coinvest-deal-tracking";
import { type DealTrackingLimitedPartnerBreakdown } from "@vega-api/generated/types/dealTrackingLimitedPartnerBreakdown";
import { NdaTrackingStatus } from "@vega-api/generated/types/ndaTrackingStatus";
import { useRevertEditOnError } from "@vega-core/components/DataGrid/useRevertEditOnError";
import { useToast } from "@vega-core/components/Toast/useToast";
import { useBoolean } from "@vega-core/hooks/useBoolean";
import { m } from "@vega-core/paraglide/messages";
import { useCoInvestErrorToast } from "@vega-modules/hooks/useCoInvestErrorToast";

import { resolveNdaStatus } from "../components/ndaStatusDisplay";

import { TRACK_PROCESS_COL_IDS } from "./useTrackProcessColumns";

export const useTrackProcessEdit = (dealId: string) => {
  const showToast = useToast();
  const showErrorToast = useCoInvestErrorToast();
  const queryClient = useQueryClient();

  const { captureCellChangeEvent } =
    useRevertEditOnError<DealTrackingLimitedPartnerBreakdown>();

  // Selecting "Signed" needs explicit confirmation, so it routes through this
  // modal instead of committing the edit directly.
  const {
    isOpen: isNdaSignModalOpen,
    setTrue: openNdaSignModal,
    setFalse: closeNdaSignModal,
  } = useBoolean(false);
  const [ndaRowToSign, setNdaRowToSign] =
    useState<DealTrackingLimitedPartnerBreakdown | null>(null);

  const invalidateTracking = () => {
    queryClient.invalidateQueries({
      queryKey: getGetDealTrackingForGpQueryKey(dealId),
    });
  };

  const mutationCallbacks = (errorMessage: string) => ({
    onSuccess: () => {
      showToast({ variant: "success", title: m.status_success() });
      invalidateTracking();
    },
    onError: (error: ErrorType<unknown>) => {
      showErrorToast(error, { defaultMessage: errorMessage });
    },
  });

  const { mutate: updateShortlistItem } = useUpdateDealShortlistItem({
    mutation: mutationCallbacks(m.dataGrid_updateError()),
  });

  const { mutate: updateTracking } = useUpdateDealOpportunityTracking({
    mutation: mutationCallbacks(m.coInvestments_nda_updateError()),
  });

  const handleCellValueChanged = captureCellChangeEvent((event, revertEdit) => {
    const row = event.data;
    const colId = event.column.getColId();

    if (colId === TRACK_PROCESS_COL_IDS.tier) {
      updateShortlistItem(
        {
          dealId,
          dealShortlistItemId: row.dealShortlistItemId,
          data: { tierLevel: Number(row.tier) },
        },
        { onError: revertEdit },
      );

      return;
    }

    if (colId === TRACK_PROCESS_COL_IDS.nda) {
      const nextStatus = resolveNdaStatus(row);

      // Signing is confirmed in a modal that persists it via the tracking
      // endpoint, so revert the optimistic edit and hand off to the modal.
      if (nextStatus === NdaTrackingStatus.SIGNED) {
        revertEdit();
        setNdaRowToSign(row);
        openNdaSignModal();

        return;
      }

      updateTracking(
        {
          dealOpportunityId: row.dealOpportunityId,
          data: { ndaTrackingStatus: nextStatus },
        },
        { onError: revertEdit },
      );
    }
  });

  return {
    handleCellValueChanged,
    confirmNdaSignedModalProps: {
      confirmVia: "trackingStatus" as const,
      dealId,
      isOpen: isNdaSignModalOpen,
      onClose: closeNdaSignModal,
      trackingItem: ndaRowToSign,
    },
  };
};
